import { AxiosResponse } from 'axios';
import React, { useContext } from 'react';
import { axiosRequest } from 'src/common/api/axios-request';
import XLRLogger from 'src/common/helpers/logger';
import ElectronHelper from '../common/electron/ElectronHelper';
import {
  EDGE_IAM_CONFIG_URL,
  POS_SERVICE_TYPE_URL,
  XLR_CONFIG_URL,
} from '../common/helpers/config';
import { ServiceType } from './service-type';
import { getCurrentXLRConfig } from './xlr-config';

export interface XlrConfigs {
  RDS_PORT?: string;
  RPS_PORT?: string;
  RPS_HOSTNAME?: string;
  RDS_HOSTNAME?: string;
  EDGE_IAM?: boolean;
}
interface MetaData {
  serviceType: ServiceType;
  xlrConfig: XlrConfigs;
}
// service type metadata that is sealed after app launch
const metadata: MetaData = {
  serviceType: ServiceType.EmeraldSellingService,
  xlrConfig: {},
};

export const getMetaData = (): MetaData => metadata;

export const setAndSealMetadata = (type: ServiceType, xlrConfig: XlrConfigs): void => {
  metadata.serviceType = type;
  metadata.xlrConfig = xlrConfig;

  Object.freeze(metadata);
};

export const getBrowserTitle = (): string => {
  return getCurrentXLRConfig().browserTitle;
};

// context created to project pos service type though app scope
export const ServiceTypeContext = React.createContext<MetaData>(getMetaData());

export const ServiceTypeContextProvider = ({ children }): JSX.Element => (
  <ServiceTypeContext.Provider value={getMetaData()}>{children}</ServiceTypeContext.Provider>
);

// function to evaluate emerald service mode
export function isEmeraldSellingService(): boolean {
  const { serviceType } = getMetaData();

  return serviceType === ServiceType.EmeraldSellingService;
}

// custom hook to evaluate emerald service mode
export function useIsEmeraldSellingService(): boolean {
  const { serviceType } = useContext(ServiceTypeContext);

  return serviceType === ServiceType.EmeraldSellingService;
}

// custom hook to evaluate R10 service mode
export function useIsR10(): boolean {
  const { serviceType } = useContext(ServiceTypeContext);

  return serviceType === ServiceType.R10;
}

export const getPOSServiceTypeFromBFF = async (): Promise<ServiceType> => {
  const baseURL = await ElectronHelper.getBaseURL();
  const response = await posServiceTypeCall(baseURL);
  const posServiceType = await response.text();
  return posServiceType as ServiceType;
};

export const getXlrConfig = async (): Promise<XlrConfigs> => {
  const baseURL = await ElectronHelper.getBaseURL();
  return axiosRequest({ url: `${baseURL}/${XLR_CONFIG_URL}`, method: 'GET' })
    .then((response: AxiosResponse<XlrConfigs>) => response.data)
    .catch(err => {
      XLRLogger.error({ message: err.reason });
      return {};
    });
};

export const getOpenIdConfig = async (): Promise<any> => {
  const baseURL = await ElectronHelper.getBaseURL();
  return axiosRequest({ url: `${baseURL}/${EDGE_IAM_CONFIG_URL}`, method: 'GET' })
    .then((response: AxiosResponse<XlrConfigs>) => response.data)
    .catch(err => {
      XLRLogger.error({ message: err.reason });
      return {};
    });
};

function posServiceTypeCall(baseURL: string = ''): Promise<Response> {
  return fetch(`${baseURL}/${POS_SERVICE_TYPE_URL}`).then(response => {
    if (response.status >= 400 && response.status < 600) {
      throw new Error(response.statusText);
    }
    return response;
  });
}
